import React, { useState, useCallback, useRef, useEffect, Fragment } from "react";
import ShotTypeModal from "../modal/ShotTypeModal";
import TableCellRank from "../table/TableCellRank";
import Utils from "../../utils/Utils";
import Checkbox from "../form/Checkbox";
import TableSkeleton from "../table/TableSkeleton";

const skeletonTableHeader = [
  {
    title: "Category Name",
    showImage: false,
    sortable: false,
    key: "Category Name",
  },
  {
    title: "Offensive",
    showImage: false,
    sortable: false,
    key: "Offensive",
  },
  {
    title: "Defensive",
    showImage: false,
    sortable: false,
    key: "Offensive",
  },
];

const shotTypeAnalysisCategories = {
  playstyles: ["Half Court", "Transition"],
  playtypes: ["P&R Ball Screen", "Off-Screen", "Cut", "Isolation"],
  shottypes: ["Finishing at the Rim", "Catch & Shoot 3PT", "Off the Dribble 3PT", "Midrange", "Post-Up"],
};

const TeamShotTypeAnalysis = ({
  sortedShotTypes,
  confPlayTypesDEF,
  confPlayTypesOFF,
  teamPlayTypesDEF,
  teamPlayTypesOFF,
  leagueType,
  year,
  conferenceTeamCount = 0,
  loading = true,
  disableConference = true,
}) => {
  const [activeShotTypeCategory, setActiveShotTypeCategory] = useState({
    key: "",
    data: [],
  });

  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [openShotTypeModal, setOpenShotTypeModal] = useState(false);
  const [activeType, setActiveType] = useState({
    DEF: teamPlayTypesDEF,
    OFF: teamPlayTypesOFF,
  });
  const shotTypeCheckboxRef = useRef(null);

  const handleCategoryClick = (catName) => {
    if (sortedShotTypes["conf"]?.[catName] && disableConference === false) {
      setActiveShotTypeCategory({
        key: catName,
        data:
          shotTypeCheckboxRef.current.checked === true
            ? sortedShotTypes["conf"][catName]
            : sortedShotTypes["player"][catName],
      });
    }
  };

  const handleCheckboxClick = useCallback(
    (e) => {
      if (e.target.checked) {
        setCheckboxChecked(true);
        setActiveType({
          DEF: confPlayTypesDEF,
          OFF: confPlayTypesOFF,
        });
      } else {
        setCheckboxChecked(false);
        setActiveType({
          DEF: teamPlayTypesDEF,
          OFF: teamPlayTypesOFF,
        });
      }
    },
    [confPlayTypesDEF, confPlayTypesOFF, teamPlayTypesDEF, teamPlayTypesOFF]
  );

  useEffect(() => {
    if (checkboxChecked) {
      setActiveType({
        DEF: confPlayTypesDEF,
        OFF: confPlayTypesOFF,
      });
    } else {
      setActiveType({
        DEF: teamPlayTypesDEF,
        OFF: teamPlayTypesOFF,
      });
    }
  }, [year, confPlayTypesDEF, confPlayTypesOFF, teamPlayTypesDEF, teamPlayTypesOFF]);

  useEffect(() => {
    if (activeShotTypeCategory.data.length > 0) {
      setOpenShotTypeModal(true);
    }
  }, [activeShotTypeCategory]);
  const rankConfig = {
    leagueType: leagueType,
    conferenceTeamCount: shotTypeCheckboxRef?.current?.checked ? conferenceTeamCount : 0,
  };

  return (
    <div className="row no-gutters">
      <div className="col-8 text-left text-md-left my-2 my-mb-3 mt-mb-0 d-flex align-items-center">
        <h4 style={{ fontSize: 36, fontFamily: "var(--font-oswald)" }}>ShotType Analysis</h4>
      </div>
      {leagueType !== "wnba" && (
        <div className="col-4 text-center text-sm-right text-md-right my-2 my-mb-3 mt-mb-0 d-flex justify-content-end">
          <Checkbox
            id="shottypeConferenceOnlyCheck"
            ref={shotTypeCheckboxRef}
            onClick={handleCheckboxClick}
            label="Conference Only"
            checked={shotTypeCheckboxRef?.current?.checked}
          />
        </div>
      )}

      {loading === true ? (
        <TableSkeleton
          tableHeader={skeletonTableHeader}
          tableRowsCount={15}
          // className="table-space"
        />
      ) : null}

      {loading === false && true ? (
        <div className="table-responsive p-3 ptY-0">
          <table className="table">
            <thead className="sticky-th-2" style={{ borderTopRightRadius: 12, top: "0px" }}>
              <tr>
                <th rowSpan="2">Category</th>
                <th colSpan={2}>Offensive</th>
                <th colSpan={2}>Defensive</th>
              </tr>
              <tr>
                <th className="sticky-th-2" style={{ borderTopLeftRadius: 0 }}>
                  SQ PPP
                </th>

                <th className="sticky-th-2">Freq %</th>

                <th className="sticky-th-2">SQ PPP</th>

                <th className="sticky-th-2" style={{ borderTopRightRadius: 0 }}>
                  Freq %
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  style={{
                    padding: "0.25rem",
                    background: "#D0D2D9",
                    color: "#0F131B",
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  PlayStyles
                </td>
                <td
                  colSpan="2"
                  style={{
                    padding: "0.25rem",
                    background: "#D0D2D9",
                    color: "#fff",
                  }}
                ></td>
                <td
                  colSpan="2"
                  style={{
                    padding: "0.25rem",
                    background: "#D0D2D9",
                    color: "#fff",
                  }}
                ></td>
              </tr>

              <Fragment>
                <tr>
                  <td style={{ color: "#0F131B", fontWeight: "bold" }} title="">
                    Half Court
                  </td>
                  <TableCellRank rankValue={activeType?.OFF["Half Court SQ PPP rank"]} {...rankConfig}>
                    {activeType?.OFF["Half Court SQ PPP"]?.toFixed(2)}
                  </TableCellRank>
                  <TableCellRank
                    rankValue={activeType?.OFF["Half Court Frequency rank"]}
                    {...rankConfig}
                    hideGradients={true}
                  >
                    {Utils.toPercent(activeType?.OFF["Half Court Frequency"])}
                  </TableCellRank>
                  <TableCellRank rankValue={activeType.DEF["Half Court SQ PPP rank"]} {...rankConfig}>
                    {activeType.DEF["Half Court SQ PPP"]?.toFixed(2)}
                  </TableCellRank>
                  <TableCellRank
                    rankValue={activeType.DEF["Half Court Frequency rank"]}
                    {...rankConfig}
                    hideGradients={true}
                  >
                    {Utils.toPercent(activeType.DEF["Half Court Frequency"])}
                  </TableCellRank>
                </tr>
              </Fragment>
              {leagueType !== "wnba" && (
                <Fragment>
                  <tr>
                    <td style={{ color: "#0F131B", fontWeight: "bold" }} title="">
                      Transition
                    </td>
                    <TableCellRank rankValue={activeType?.OFF["Transition SQ PPP rank"]} {...rankConfig}>
                      {activeType?.OFF["Transition SQ PPP"]?.toFixed(2)}
                    </TableCellRank>
                    <TableCellRank
                      rankValue={activeType?.OFF["Transition Frequency rank"]}
                      {...rankConfig}
                      hideGradients={true}
                    >
                      {Utils.toPercent(activeType?.OFF["Transition Frequency"])}
                    </TableCellRank>
                    <TableCellRank rankValue={activeType.DEF["Transition SQ PPP rank"]} {...rankConfig}>
                      {activeType.DEF["Transition SQ PPP"]?.toFixed(2)}
                    </TableCellRank>
                    <TableCellRank
                      rankValue={activeType.DEF["Transition Frequency rank"]}
                      {...rankConfig}
                      hideGradients={true}
                    >
                      {Utils.toPercent(activeType.DEF["Transition Frequency"])}
                    </TableCellRank>
                  </tr>
                </Fragment>
              )}
              {/* {leagueType !== "wnba" && ( */}
              <tr>
                <td
                  style={{
                    padding: "0.25rem",
                    background: "#D0D2D9",
                    color: "#0F131B",
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  PlayTypes
                </td>
                <td
                  colSpan="2"
                  style={{
                    padding: "0.25rem",
                    background: "#D0D2D9",
                    color: "#fff",
                  }}
                ></td>
                <td
                  colSpan="2"
                  style={{
                    padding: "0.25rem",
                    background: "#D0D2D9",
                    color: "#fff",
                  }}
                ></td>
              </tr>
              {/* )} */}
              {shotTypeAnalysisCategories["playtypes"].map((category, i) => {
                if (
                  leagueType === "wnba" &&
                  (category === "P&R Ball Screen" || category === "Off-Screen" || category === "Isolation")
                ) {
                  return null;
                } else {
                  return (
                    <tr key={`platypes${i + 1}`}>
                      <td style={{ color: "#434343", fontWeight: "bold" }}>{category}</td>

                      <TableCellRank rankValue={activeType?.OFF[category + " SQ PPP rank"]} {...rankConfig}>
                        {activeType?.OFF[category + " SQ PPP"]?.toFixed(2)}
                      </TableCellRank>
                      <TableCellRank
                        rankValue={activeType?.OFF[category + " Frequency rank"]}
                        {...rankConfig}
                        hideGradients={true}
                      >
                        {Utils.toPercent(activeType?.OFF[category + " Frequency"])}
                      </TableCellRank>
                      <TableCellRank rankValue={activeType.DEF[category + " SQ PPP rank"]} {...rankConfig}>
                        {activeType?.DEF[category + " SQ PPP"]?.toFixed(2)}
                      </TableCellRank>
                      <TableCellRank
                        rankValue={activeType.DEF[category + " Frequency rank"]}
                        {...rankConfig}
                        hideGradients={true}
                      >
                        {Utils.toPercent(activeType.DEF[category + " Frequency"])}
                      </TableCellRank>
                    </tr>
                  );
                }
              })}

              <tr>
                <td
                  style={{
                    padding: "0.25rem",
                    background: "#D0D2D9",
                    color: "#0F131B",
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  ShotTypes
                </td>
                <td
                  colSpan="2"
                  style={{
                    padding: "0.25rem",
                    background: "#D0D2D9",
                    color: "#0F131B",
                  }}
                ></td>
                <td
                  colSpan="2"
                  style={{
                    padding: "0.25rem",
                    background: "#D0D2D9",
                    color: "#fff",
                  }}
                ></td>
              </tr>

              <>
                <tr>
                  <td style={{ color: "#434343", fontWeight: "bold" }}>Finishing at the Rim</td>
                  <TableCellRank
                    rankValue={activeType?.OFF["Finishing at the Rim SQ PPP rank"]}
                    {...rankConfig}
                    testProp="props"
                  >
                    {activeType?.OFF["Finishing at the Rim SQ PPP"]?.toFixed(2)}
                  </TableCellRank>
                  <TableCellRank
                    rankValue={activeType?.OFF["Finishing at the Rim Frequency rank"]}
                    {...rankConfig}
                    hideGradients={true}
                  >
                    {Utils.toPercent(activeType?.OFF["Finishing at the Rim Frequency"])}
                  </TableCellRank>
                  <TableCellRank rankValue={activeType.DEF["Finishing at the Rim SQ PPP rank"]} {...rankConfig}>
                    {activeType.DEF["Finishing at the Rim SQ PPP"]?.toFixed(2)}
                  </TableCellRank>
                  <TableCellRank
                    rankValue={activeType.DEF["Finishing at the Rim Frequency rank"]}
                    {...rankConfig}
                    hideGradients={true}
                  >
                    {Utils.toPercent(activeType.DEF["Finishing at the Rim Frequency"])}
                  </TableCellRank>
                </tr>
              </>
              {leagueType !== "wnba" && (
                <>
                  <tr>
                    <td style={{ color: "#434343", fontWeight: "bold" }}>Catch & Shoot 3PT</td>
                    <TableCellRank
                      rankValue={activeType?.OFF["Catch & Shoot 3PT SQ PPP rank"]}
                      {...rankConfig}
                      testProp="props"
                    >
                      {activeType?.OFF["Catch & Shoot 3PT SQ PPP"]?.toFixed(2)}
                    </TableCellRank>
                    <TableCellRank
                      rankValue={activeType?.OFF["Catch & Shoot 3PT Frequency rank"]}
                      {...rankConfig}
                      hideGradients={true}
                    >
                      {Utils.toPercent(activeType?.OFF["Catch & Shoot 3PT Frequency"])}
                    </TableCellRank>
                    <TableCellRank rankValue={activeType.DEF["Catch & Shoot 3PT SQ PPP rank"]} {...rankConfig}>
                      {activeType.DEF["Catch & Shoot 3PT SQ PPP"]?.toFixed(2)}
                    </TableCellRank>
                    <TableCellRank
                      rankValue={activeType.DEF["Catch & Shoot 3PT Frequency rank"]}
                      {...rankConfig}
                      hideGradients={true}
                    >
                      {Utils.toPercent(activeType.DEF["Catch & Shoot 3PT Frequency"])}
                    </TableCellRank>
                  </tr>

                  <tr>
                    <td style={{ color: "#434343", fontWeight: "bold" }}>Off the Dribble 3PT</td>
                    <TableCellRank
                      rankValue={activeType?.OFF["Off the Dribble 3PT SQ PPP rank"]}
                      {...rankConfig}
                      testProp="props"
                    >
                      {activeType?.OFF["Off the Dribble 3PT SQ PPP"]?.toFixed(2)}
                    </TableCellRank>
                    <TableCellRank
                      rankValue={activeType?.OFF["Off the Dribble 3PT Frequency rank"]}
                      {...rankConfig}
                      hideGradients={true}
                    >
                      {Utils.toPercent(activeType?.OFF["Off the Dribble 3PT Frequency"])}
                    </TableCellRank>
                    <TableCellRank rankValue={activeType.DEF["Off the Dribble 3PT SQ PPP rank"]} {...rankConfig}>
                      {activeType.DEF["Off the Dribble 3PT SQ PPP"]?.toFixed(2)}
                    </TableCellRank>
                    <TableCellRank
                      rankValue={activeType.DEF["Off the Dribble 3PT Frequency rank"]}
                      {...rankConfig}
                      hideGradients={true}
                    >
                      {Utils.toPercent(activeType.DEF["Off the Dribble 3PT Frequency"])}
                    </TableCellRank>
                  </tr>
                </>
              )}

              <>
                <tr>
                  <td style={{ color: "#434343", fontWeight: "bold" }}>Midrange</td>
                  <TableCellRank rankValue={activeType?.OFF["Midrange SQ PPP rank"]} {...rankConfig} testProp="props">
                    {activeType?.OFF["Midrange SQ PPP"]?.toFixed(2)}
                  </TableCellRank>
                  <TableCellRank
                    rankValue={activeType?.OFF["Midrange Frequency rank"]}
                    {...rankConfig}
                    hideGradients={true}
                  >
                    {Utils.toPercent(activeType?.OFF["Midrange Frequency"])}
                  </TableCellRank>
                  <TableCellRank rankValue={activeType.DEF["Midrange SQ PPP rank"]} {...rankConfig}>
                    {activeType.DEF["Midrange SQ PPP"]?.toFixed(2)}
                  </TableCellRank>
                  <TableCellRank
                    rankValue={activeType.DEF["Midrange Frequency rank"]}
                    {...rankConfig}
                    hideGradients={true}
                  >
                    {Utils.toPercent(activeType.DEF["Midrange Frequency"])}
                  </TableCellRank>
                </tr>
              </>

              <>
                <tr>
                  <td style={{ color: "#434343", fontWeight: "bold" }}>Post-Up</td>
                  <TableCellRank rankValue={activeType?.OFF["Post-Up SQ PPP rank"]} {...rankConfig} testProp="props">
                    {activeType?.OFF["Post-Up SQ PPP"]?.toFixed(2)}
                  </TableCellRank>
                  <TableCellRank
                    rankValue={activeType?.OFF["Post-Up Frequency rank"]}
                    {...rankConfig}
                    hideGradients={true}
                  >
                    {Utils.toPercent(activeType?.OFF["Post-Up Frequency"])}
                  </TableCellRank>
                  <TableCellRank rankValue={activeType.DEF["Post-Up SQ PPP rank"]} {...rankConfig}>
                    {activeType.DEF["Post-Up SQ PPP"]?.toFixed(2)}
                  </TableCellRank>
                  <TableCellRank
                    rankValue={activeType.DEF["Post-Up Frequency rank"]}
                    {...rankConfig}
                    hideGradients={true}
                  >
                    {Utils.toPercent(activeType.DEF["Post-Up Frequency"])}
                  </TableCellRank>
                </tr>
              </>
            </tbody>
          </table>
        </div>
      ) : null}
      <ShotTypeModal
        open={openShotTypeModal}
        setOpen={setOpenShotTypeModal}
        data={activeShotTypeCategory}
        {...{ leagueType, year }}
      />
    </div>
  );
};

export default TeamShotTypeAnalysis;
